import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Upload, Loader2, RefreshCw, FileInput, Mail, Settings as SettingsIcon,
  ExternalLink,
} from "lucide-react";

const fmtMoney = (n) =>
  (Number(n) || 0).toLocaleString("en-AU",
    { style: "currency", currency: "AUD" });

const STATUS_STYLE = {
  review: "bg-amber-50 text-amber-800 border-amber-200",
  pushed: "bg-sky-50 text-sky-800 border-sky-200",
  paid: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

const STATUS_LABEL = {
  review: "Needs review",
  pushed: "Pushed to Xero",
  paid: "Paid",
};

function BillsList({ onOpenSettings }) {
  const nav = useNavigate();
  const fileInput = useRef(null);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status !== "all") params.set("status", status);
      if (search) params.set("search", search);
      const [s, l] = await Promise.all([
        api.get("/bills/summary"),
        api.get(`/bills?${params}`),
      ]);
      setSummary(s.data);
      setRows(l.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load bills");
    } finally {
      setLoading(false);
    }
  }, [status, search]);
  useEffect(() => { load(); }, [load]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-uploading same file
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/bills/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Extracted: ${data.supplier_name} — ${fmtMoney(data.total)}`);
      nav(`/bills/${data.bill_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "OCR upload failed");
    } finally {
      setUploading(false);
    }
  };

  const cards = [
    ["review", "Needs review", "text-amber-700"],
    ["pushed", "Pushed to Xero", "text-sky-700"],
    ["paid", "Paid", "text-emerald-700"],
  ];

  return (
    <div className="space-y-4" data-testid="bills-list">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {cards.map(([k, label, cls]) => (
          <Card key={k} data-testid={`bills-summary-${k}`}>
            <CardContent className="p-4">
              <div className="text-xs text-zinc-500">{label}</div>
              <div className={`text-xl font-bold ${cls}`}>
                {fmtMoney(summary?.[k]?.balance_due)}
              </div>
              <div className="text-xs text-zinc-400">
                {summary?.[k]?.count ?? 0} bill(s) · total {fmtMoney(summary?.[k]?.total)}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Search supplier / invoice # / reference…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          className="w-64" data-testid="bills-search-input" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44" data-testid="bills-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="review">Needs review</SelectItem>
            <SelectItem value="pushed">Pushed to Xero</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="icon" onClick={load}
          data-testid="bills-refresh-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" />
                   : <RefreshCw className="h-4 w-4" />}
        </Button>
        <div className="flex-1" />
        <Button variant="outline" onClick={onOpenSettings}
          data-testid="bills-open-settings">
          <SettingsIcon className="h-4 w-4 mr-1" /> Settings & Inbound Email
        </Button>
        <input ref={fileInput} type="file"
          accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif"
          onChange={onUpload} className="hidden"
          data-testid="bills-upload-file-input" />
        <Button onClick={() => fileInput.current?.click()} disabled={uploading}
          data-testid="bills-upload-btn">
          {uploading
            ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> OCR running…</>
            : <><Upload className="h-4 w-4 mr-1" /> Upload bill (PDF/image)</>}
        </Button>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-zinc-50 text-left text-xs text-zinc-500">
                <th className="p-3">Supplier</th>
                <th className="p-3">Invoice #</th>
                <th className="p-3">Issued</th>
                <th className="p-3">Due</th>
                <th className="p-3 text-right">Total</th>
                <th className="p-3 text-right">Balance</th>
                <th className="p-3">Xero org</th>
                <th className="p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.bill_id}
                  className="border-b hover:bg-blue-50/40 cursor-pointer"
                  data-testid={`bill-row-${b.bill_id}`}
                  onClick={() => nav(`/bills/${b.bill_id}`)}>
                  <td className="p-3 font-medium text-[#002FA7]">
                    {b.supplier_name}
                    {b.source === "email" && (
                      <Mail className="inline-block h-3.5 w-3.5 ml-2 text-zinc-400" />
                    )}
                  </td>
                  <td className="p-3">{b.invoice_number || "—"}</td>
                  <td className="p-3">{b.issue_date || "—"}</td>
                  <td className="p-3">{b.due_date || "—"}</td>
                  <td className="p-3 text-right">{fmtMoney(b.total)}</td>
                  <td className="p-3 text-right font-medium">
                    {fmtMoney(b.balance_due)}
                  </td>
                  <td className="p-3 text-xs text-zinc-500">
                    {b.xero_tenant_name || (b.xero_tenant_id ? "—" : "not set")}
                  </td>
                  <td className="p-3">
                    <Badge variant="outline"
                      className={STATUS_STYLE[b.status] ||
                                 "bg-zinc-50 text-zinc-700 border-zinc-200"}>
                      {STATUS_LABEL[b.status] || b.status}
                    </Badge>
                  </td>
                </tr>
              ))}
              {!rows.length && (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-zinc-400">
                    No bills yet — upload a PDF or forward one to your
                    inbound-parse email address.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function BillsSettingsTab({ onDone }) {
  const [s, setS] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [saving, setSaving] = useState(false);
  const [log, setLog] = useState([]);
  const publicBase = process.env.REACT_APP_BACKEND_URL || "";

  const load = useCallback(async () => {
    try {
      const [ss, tt, ll] = await Promise.all([
        api.get("/bills/settings"),
        api.get("/bills/xero-tenants"),
        api.get("/bills/inbound/log"),
      ]);
      setS(ss.data || {});
      setTenants(tt.data || []);
      setLog(ll.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load settings");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/bills/settings", s || {});
      toast.success("Bills settings saved");
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const webhookUrl = `${publicBase}/api/bills/inbound/sendgrid`;

  if (!s) return <div className="p-8 text-center text-zinc-400">Loading…</div>;

  return (
    <div className="grid gap-4 lg:grid-cols-2" data-testid="bills-settings">
      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-zinc-700">
            <Mail className="h-4 w-4" /> Inbound Email (SendGrid Inbound Parse)
          </div>
          <p className="text-xs text-zinc-500 leading-relaxed">
            Forward supplier bills as email attachments (PDF or image) to a
            dedicated mailbox. SendGrid's Inbound Parse posts them to the
            webhook URL below, and every attachment is OCR&#8209;processed
            into a draft bill for your review.
          </p>
          <div className="space-y-2">
            <Label>Inbound mailbox (address you forward to)</Label>
            <Input value={s.inbound_email || ""}
              placeholder="bills@bills.icebergdigital.com.au"
              onChange={(e) => setS({ ...s, inbound_email: e.target.value })}
              data-testid="bills-inbound-email-input" />
            <p className="text-[11px] text-zinc-400">
              Set the matching MX record on this subdomain to
              <code className="mx-1 bg-zinc-100 px-1 rounded">mx.sendgrid.net</code>
              and add the same address on SendGrid → Settings → Inbound Parse.
            </p>
          </div>
          <div className="space-y-2">
            <Label>SendGrid Inbound Parse Destination URL</Label>
            <Input value={webhookUrl} readOnly
              onClick={(e) => e.target.select()}
              className="font-mono text-xs"
              data-testid="bills-inbound-webhook-url" />
            <Button variant="outline" size="sm"
              onClick={() => {
                navigator.clipboard?.writeText(webhookUrl);
                toast.success("Webhook URL copied");
              }}>Copy</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-zinc-700">
            <SettingsIcon className="h-4 w-4" /> Xero defaults
          </div>
          <p className="text-xs text-zinc-500 leading-relaxed">
            Optional. If set, new bills are pre&#8209;populated with this
            organisation and AP account code — you can still change either
            per bill before pushing to Xero.
          </p>
          <div className="space-y-2">
            <Label>Default Xero organisation</Label>
            <Select value={s.default_tenant_id || "__none__"}
              onValueChange={(v) => setS({
                ...s,
                default_tenant_id: v === "__none__" ? "" : v,
                default_tenant_name:
                  tenants.find((t) => t.tenant_id === v)?.tenant_name || "",
              })}>
              <SelectTrigger data-testid="bills-default-tenant">
                <SelectValue placeholder="Not set — pick per bill" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Not set — pick per bill</SelectItem>
                {tenants.map((t) => (
                  <SelectItem key={t.tenant_id} value={t.tenant_id}>
                    {t.tenant_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!tenants.length && (
              <p className="text-[11px] text-amber-700">
                No Xero organisations connected yet.
                <a href="/integrations/xero" className="underline ml-1">
                  Connect Xero &rarr;
                </a>
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label>Default AP account code</Label>
              <Input value={s.default_ap_account_code || ""}
                placeholder="e.g. 800"
                onChange={(e) => setS({ ...s, default_ap_account_code: e.target.value })}
                data-testid="bills-default-ap-code" />
              <p className="text-[11px] text-zinc-400">
                Blank = pick per bill.
              </p>
            </div>
            <div className="space-y-1">
              <Label>Default payment (bank) account code</Label>
              <Input value={s.default_payment_account_code || ""}
                placeholder="e.g. 090"
                onChange={(e) => setS({ ...s, default_payment_account_code: e.target.value })}
                data-testid="bills-default-pay-code" />
              <p className="text-[11px] text-zinc-400">
                Used when recording payments; must have &quot;enable payments&quot; on.
              </p>
            </div>
          </div>
          <div className="pt-2">
            <Button onClick={save} disabled={saving} data-testid="bills-settings-save">
              {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Saving</>
                      : "Save settings"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="lg:col-span-2">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold text-zinc-700 flex items-center gap-2">
              <FileInput className="h-4 w-4" /> Recent inbound emails
            </div>
            <Button variant="outline" size="sm" onClick={load}>
              <RefreshCw className="h-4 w-4 mr-1" /> Refresh
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-zinc-500 border-b">
                  <th className="p-2">Received</th>
                  <th className="p-2">From</th>
                  <th className="p-2">Subject</th>
                  <th className="p-2">Attachments</th>
                  <th className="p-2">Bills created</th>
                </tr>
              </thead>
              <tbody>
                {log.map((r) => (
                  <tr key={r.log_id} className="border-b">
                    <td className="p-2 text-xs">{(r.received_at || "").slice(0, 19).replace("T", " ")}</td>
                    <td className="p-2">{r.from}</td>
                    <td className="p-2">{r.subject || "—"}</td>
                    <td className="p-2 text-xs">{(r.attachments || []).join(", ") || "—"}</td>
                    <td className="p-2 text-xs">{(r.bills_created || []).length}</td>
                  </tr>
                ))}
                {!log.length && (
                  <tr><td colSpan={5} className="p-6 text-center text-zinc-400">
                    Nothing received yet.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function BillsPage() {
  const [tab, setTab] = useState("list");
  return (
    <div className="p-6 space-y-4" data-testid="bills-page">
      <div className="flex items-center gap-3">
        <FileInput className="h-6 w-6 text-[#002FA7]" />
        <h1 className="text-2xl font-bold text-zinc-800">Bills — Accounts Payable</h1>
        <a href="https://developer.xero.com/documentation/api/accounting/invoices/"
          target="_blank" rel="noopener noreferrer"
          className="ml-2 text-xs text-zinc-400 flex items-center gap-1">
          Xero ACCPAY docs <ExternalLink className="h-3 w-3" />
        </a>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="list" data-testid="bills-tab-list">Bills</TabsTrigger>
          <TabsTrigger value="settings" data-testid="bills-tab-settings">
            Settings & Inbound Email
          </TabsTrigger>
        </TabsList>
        <TabsContent value="list" className="pt-4">
          <BillsList onOpenSettings={() => setTab("settings")} />
        </TabsContent>
        <TabsContent value="settings" className="pt-4">
          <BillsSettingsTab onDone={() => setTab("list")} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
